import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type {
  ReviewQueueAppealItem,
  ReviewQueueConfigItem,
  ReviewQueueCreativeItem,
  ReviewQueueItem,
  ReviewQueueKind,
  ReviewQueueSiteToolsItem,
  ReviewQueueSlotVersionItem,
  ReviewSiteToolsResponse,
  ReviewSlotVersionResponse,
  ReviewVersionResponse,
} from "../../api/staffTypes";
import { useStaffSession } from "../../auth/guards";
import { Badge, Banner, Button, Card, Checkbox, EmptyState, Input, Money, PageHeader, QueryView, RadioGroup, StatusPill, Table, Textarea } from "../../components/ui";
import { staffKeys } from "../../layout/StaffShell";
import { FilterTabs, NotAppliedBanner, reasonError, shortId, StaffRejection, StepUpNote, useStaffCommand, when } from "./staffCommon";

// The review queue (G-19, migration 0061): pending config and creative
// versions and advertiser appeals, oldest first (review_queue.go:35-198),
// and the decisions: POST /v1/internal/{config,creative}-versions/{id}/review
// (review.go:52-108, always 200 — applied:false is the refusal) and POST
// /v1/internal/appeals/{id}/decide (review_queue.go:207-244, step-up, 422
// typed refusals). Creative text and the destination are shown as inert
// text: nothing the advertiser wrote is rendered as HTML or linked
// (UX-DESIGN.md §5: the review tool must not become a vector).

const KIND_LABEL: Record<ReviewQueueKind, string> = {
  config_version: "Targeting & bid",
  creative_version: "Creative",
  appeal: "Appeal",
  site_tools: "Declared tools",
  slot_version: "Slot version",
};

function itemId(i: ReviewQueueItem): string {
  if (i.kind === "appeal") return i.appeal_id;
  if (i.kind === "site_tools") return i.declaration_id;
  if (i.kind === "slot_version") return i.slot_version_id;
  return i.version_id;
}

/** The publisher/advertiser or campaign id shown in the queue's "Campaign" column, whichever kind the row is. */
function itemSubject(i: ReviewQueueItem): string {
  if (i.kind === "site_tools" || i.kind === "slot_version") return i.publisher_id;
  return i.campaign_id;
}

function itemLabel(i: ReviewQueueItem): string {
  if (i.kind === "appeal") return `Appeal on ${i.version_kind.replace("_", " ")} ${shortId(i.version_id)}`;
  if (i.kind === "site_tools") return `${i.domain} · version ${i.version_no}`;
  if (i.kind === "slot_version") return `${i.tool_name} (${i.domain}) · version ${i.version_no}`;
  return `Version ${i.version_no} · ${shortId(i.version_id)}`;
}

export function ReviewQueuePage() {
  const ep = useEndpoints();
  const [params, setParams] = useSearchParams();
  const kind = (params.get("kind") as ReviewQueueKind | null) ?? null;
  const queue = useQuery({ queryKey: staffKeys.reviewQueue, queryFn: () => ep.staff.review.queue() });

  return (
    <div className="stack-lg">
      <PageHeader title="Review queue" description="Pending versions and appeals, oldest first. At most 100 of each kind are listed." />
      <FilterTabs
        label="Queue kind"
        value={kind ?? "all"}
        onChange={(k) => setParams(k === "all" ? {} : { kind: k })}
        options={(["all", "config_version", "creative_version", "appeal", "site_tools", "slot_version"] as const).map((k) => {
          const n = queue.data?.items.filter((i) => k === "all" || i.kind === k).length;
          return {
            value: k,
            label: (
              <>
                {k === "all" ? "All" : KIND_LABEL[k]} {n !== undefined ? <Badge>{n}</Badge> : null}
              </>
            ),
          };
        })}
      />
      <QueryView query={queue} loadingLabel="Loading the review queue">
        {(data) => {
          const rows = data.items.filter((i) => !kind || i.kind === kind);
          return (
            <Table
              caption={`Review queue as of ${when(data.as_of)}`}
              rows={rows}
              rowKey={itemId}
              empty={<EmptyState title="Nothing waiting">Every version and appeal has been decided.</EmptyState>}
              columns={[
                { key: "kind", header: "Kind", cell: (i) => KIND_LABEL[i.kind] },
                {
                  key: "what",
                  header: "Item",
                  cell: (i) => <Link to={`/staff/review/${i.kind}/${itemId(i)}`}>{itemLabel(i)}</Link>,
                },
                {
                  key: "campaign",
                  header: "Campaign / publisher",
                  cell: (i) => <span className="mono">{shortId(itemSubject(i))}</span>,
                },
                { key: "age", header: "Waiting since", cell: (i) => when(i.created_at) },
                { key: "flags", header: "Flags", cell: (i) => <QueueFlags item={i} /> },
              ]}
            />
          );
        }}
      </QueryView>
    </div>
  );
}

function QueueFlags({ item }: { item: ReviewQueueItem }) {
  if (item.kind === "config_version") {
    return (
      <span className="row">
        {item.requires_regulated_confirmation ? <StatusPill tone="warning">Regulated: {item.regulated_categories.join(", ")}</StatusPill> : null}
        {!item.submitted ? <Badge>Terms not accepted</Badge> : null}
      </span>
    );
  }
  if (item.kind === "creative_version") {
    return (
      <span className="row">
        {item.automated_flags.map((f) => (
          <StatusPill key={f.category} tone="warning">
            {f.category}
          </StatusPill>
        ))}
        {!item.destination.domain_verified ? <StatusPill tone="error">Domain not verified</StatusPill> : null}
      </span>
    );
  }
  if (item.kind === "site_tools") {
    return (
      <span className="row">
        {!item.domain_verified ? <StatusPill tone="warning">Domain not verified</StatusPill> : null}
        {item.currently_approved ? <Badge>Change to an approved set</Badge> : <Badge>First declaration</Badge>}
      </span>
    );
  }
  if (item.kind === "slot_version") {
    return <span className="small muted">Pricing model change{item.live_version ? "" : " (no live version yet)"}</span>;
  }
  return item.rejection ? <span className="small muted">Rejected {when(item.rejection.rejected_at)}</span> : null;
}

export function ReviewItemPage() {
  const { kind = "", id = "" } = useParams();
  const ep = useEndpoints();
  const queue = useQuery({ queryKey: staffKeys.reviewQueue, queryFn: () => ep.staff.review.queue() });
  return (
    <div className="stack-lg">
      <PageHeader title={`${KIND_LABEL[kind as ReviewQueueKind] ?? "Review"} ${shortId(id)}`} back={{ to: "/staff/review", label: "Review queue" }} />
      <QueryView query={queue} loadingLabel="Loading the item">
        {(data) => {
          const item = data.items.find((i) => i.kind === kind && itemId(i) === id);
          if (!item) {
            return (
              <Banner tone="info" title="Not in the queue">
                This item isn't waiting any more: it was decided, withdrawn or superseded, or it's beyond the first 100 of its kind.
              </Banner>
            );
          }
          if (item.kind === "config_version") return <ConfigReview item={item} />;
          if (item.kind === "creative_version") return <CreativeReview item={item} />;
          if (item.kind === "site_tools") return <SiteToolsReview item={item} />;
          if (item.kind === "slot_version") return <SlotVersionReview item={item} />;
          return <AppealDecision item={item} />;
        }}
      </QueryView>
    </div>
  );
}

function ConfigReview({ item }: { item: ReviewQueueConfigItem }) {
  const categories = item.targeting.categories ?? [];
  return (
    <div className="stack">
      <Card title="Targeting and bid">
        <dl className="definition-list">
          <dt>Version</dt>
          <dd>
            {item.version_no} · <span className="mono">{item.version_id}</span> · {item.status.replace("_", " ")}
          </dd>
          <dt>Campaign / advertiser</dt>
          <dd className="mono">
            {item.campaign_id} / {item.advertiser_id}
          </dd>
          <dt>Bid</dt>
          <dd>
            <Money value={item.bid_amount} currency={item.currency} /> {item.pricing_model.toUpperCase()}
          </dd>
          <dt>Categories</dt>
          <dd>{categories.length ? categories.join(", ") : "None (untargeted)"}</dd>
          <dt>Category exclusions</dt>
          <dd>{item.category_exclusions?.length ? item.category_exclusions.join(", ") : "None"}</dd>
          <dt>Publisher exclusions</dt>
          <dd className="mono">{item.publisher_exclusions?.length ? item.publisher_exclusions.join(", ") : "None"}</dd>
          <dt>Terms accepted</dt>
          <dd>{item.submitted ? "Yes" : "No"}</dd>
          <dt>Waiting since</dt>
          <dd>{when(item.created_at)}</dd>
        </dl>
      </Card>
      <VersionDecision kind="config_version" id={item.version_id} regulated={item.regulated_categories} />
    </div>
  );
}

// MIGRATION 0078 (owner decision 2026-09-27): domain control is no
// longer required by default, so this card now shows what a reviewer can
// actually act on instead — the shared-platform page identity (not just
// the host, since a shared platform can hold many unrelated pages on one
// host), whether the destination is currently suspended, and the most
// recent scheduled scan's redirect-chain evidence: the resolved final
// URL, the full chain, and a prominent warning when the final host
// doesn't match what was submitted. Domain-control verification is still
// shown, but as optional information, never as a blocking requirement
// (the review decision itself is refused server-side when the policy is
// on and it's missing — this card just stops implying it's always
// required).
function CreativeReview({ item }: { item: ReviewQueueCreativeItem }) {
  const d = item.destination;
  return (
    <div className="stack">
      <Card title="Creative">
        <dl className="definition-list">
          <dt>Version</dt>
          <dd>
            {item.version_no} · <span className="mono">{item.version_id}</span> · {item.format}
          </dd>
          <dt>Title</dt>
          <dd>
            <pre className="staff-inert">{item.title}</pre>
          </dd>
          <dt>Body</dt>
          <dd>
            <pre className="staff-inert">{item.body}</pre>
          </dd>
          <dt>Destination submitted</dt>
          <dd>
            <span className="mono break-all">{item.destination_url}</span>
          </dd>
          <dt>Shared-platform page identity</dt>
          <dd>
            <span className="mono break-all">{d.identity_key}</span>
            {d.identity_key !== d.host ? <span className="xs muted"> (host: {d.host})</span> : null}
          </dd>
          <dt>Domain control</dt>
          <dd>
            {d.domain_verified ? (
              <StatusPill tone="success">Verified (optional info)</StatusPill>
            ) : (
              <StatusPill tone="neutral">Not verified — not required unless the policy requires it</StatusPill>
            )}{" "}
            {d.suspended ? <StatusPill tone="error">Destination suspended</StatusPill> : null}
          </dd>
          <dt>Resolved final URL</dt>
          <dd>
            {d.resolved_final_url ? (
              <>
                <span className="mono break-all">{d.resolved_final_url}</span>
                {d.redirect_chain && d.redirect_chain.length > 1 ? (
                  <p className="xs muted">
                    Redirect chain:{" "}
                    {d.redirect_chain.map((u, idx) => (
                      <span key={idx} className="mono break-all">
                        {idx > 0 ? " → " : ""}
                        {u}
                      </span>
                    ))}
                  </p>
                ) : null}
              </>
            ) : (
              <span className="small muted">Not yet scanned — this destination hasn't reached a live campaign version yet.</span>
            )}
          </dd>
          <dt>Automated flags</dt>
          <dd>{item.automated_flags.length ? item.automated_flags.map((f) => f.category).join(", ") : "None"}</dd>
          <dt>Terms accepted</dt>
          <dd>{item.submitted ? "Yes" : "No"}</dd>
        </dl>
        {d.final_host_mismatch ? (
          <Banner tone="warning" title="Final destination does not match what was submitted">
            After following redirects, this destination lands on a different host than the one submitted. This can be a normal
            tracking/affiliate redirect, or it can mean the advertiser no longer controls (or never controlled) the page they
            claimed. Check the resolved final URL above before approving.
          </Banner>
        ) : null}
        <p className="xs muted">
          Shown as plain text: nothing here is rendered or fetched. The destination isn't a link; open it only in an isolated
          browsing session.
        </p>
      </Card>
      <VersionDecision kind="creative_version" id={item.version_id} regulated={[]} />
    </div>
  );
}

/**
 * PLAN.md §9 / migration 0077: the site's decision-endpoint rate limit
 * (20 req/s, burst 100 by default, per site and tool) and a staff
 * override, shown next to the site's other review-console detail since
 * there's no separate site-detail page in the staff console today.
 */
function SiteRateLimitControl({ siteId }: { siteId: string }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const key = ["staff", "site-rate-limit", siteId];
  const limit = useQuery({ queryKey: key, queryFn: () => ep.staff.siteRateLimit.get(siteId) });
  const [rate, setRate] = useState("");
  const [burst, setBurst] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand(
    (req: { clear: true } | { rate_per_sec: string; burst: number }, opts) => ep.staff.siteRateLimit.set(siteId, req, opts),
    () => void qc.invalidateQueries({ queryKey: key }),
  );

  function submitSet(e: FormEvent) {
    e.preventDefault();
    const r = rate.trim();
    const b = Number.parseInt(burst.trim(), 10);
    if (!/^\d{1,7}(\.\d{1,3})?$/.test(r) || Number.parseFloat(r) <= 0) {
      return setFormError("Enter a positive rate (requests/second), e.g. 20 or 20.5.");
    }
    if (!Number.isInteger(b) || b <= 0) {
      return setFormError("Enter a positive whole-number burst.");
    }
    setFormError(null);
    cmd.run({ rate_per_sec: r, burst: b });
  }

  function clear() {
    setFormError(null);
    cmd.run({ clear: true });
  }

  return (
    <Card title="Decision rate limit">
      <QueryView query={limit} loadingLabel="Loading the rate limit">
        {(data) => (
          <div className="stack-sm">
            <dl className="definition-list">
              <dt>Current limit</dt>
              <dd>
                {data.override ? (
                  <>
                    {data.override.rate_per_sec} req/s, burst {data.override.burst}{" "}
                    <Badge>staff override</Badge>
                  </>
                ) : (
                  <>
                    {data.default_rate_per_sec} req/s, burst {data.default_burst} <span className="small muted">(platform default)</span>
                  </>
                )}
              </dd>
            </dl>
            <form className="row" style={{ gap: "var(--space-2)", alignItems: "flex-end" }} onSubmit={submitSet} noValidate>
              <Input label="Rate (req/s)" value={rate} onChange={(e) => setRate(e.target.value)} placeholder={data.default_rate_per_sec} />
              <Input label="Burst" value={burst} onChange={(e) => setBurst(e.target.value)} placeholder={String(data.default_burst)} />
              <Button type="submit" variant="secondary" loading={cmd.isPending}>
                Set override
              </Button>
              {data.override ? (
                <Button type="button" variant="secondary" onClick={clear} loading={cmd.isPending}>
                  Clear override
                </Button>
              ) : null}
            </form>
            <StepUpNote required={false} />
            {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
            {cmd.error ? <StaffRejection error={cmd.error} /> : null}
          </div>
        )}
      </QueryView>
    </Card>
  );
}

/** G-33 console: approve or reject a site's declared tool set, migration 0066's review_site_tool_declaration. */
function SiteToolsReview({ item }: { item: ReviewQueueSiteToolsItem }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [decision, setDecision] = useState<"approve" | "reject" | "">("");
  const [reason, setReason] = useState("");
  const [classification, setClassification] = useState<Array<{ name: string; category: string }>>(item.tools.map((t) => ({ name: t.name, category: t.category ?? "" })));
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand(
    (req: { decision: "approve" | "reject"; reason: string; classification?: Array<{ name: string; category: string | null }> }, opts) =>
      ep.staff.review.siteTools(item.declaration_id, req, opts),
    (res: ReviewSiteToolsResponse) => {
      if (res.applied) void qc.invalidateQueries({ queryKey: staffKeys.reviewQueue });
    },
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!decision) return setFormError("Choose approve or reject.");
    const re = reasonError(reason);
    if (re) return setFormError(re);
    setFormError(null);
    cmd.run({
      decision,
      reason: reason.trim(),
      ...(decision === "approve" ? { classification: classification.map((c) => ({ name: c.name, category: c.category.trim() || null })) } : {}),
    });
  }

  const res = cmd.data;
  return (
    <div className="stack">
      <Card title="Declared tools">
        <dl className="definition-list">
          <dt>Site</dt>
          <dd>
            {item.domain} · publisher <span className="mono">{shortId(item.publisher_id)}</span>
          </dd>
          <dt>Domain control</dt>
          <dd>{item.domain_verified ? <StatusPill tone="success">Verified</StatusPill> : <StatusPill tone="warning">Not verified</StatusPill>}</dd>
          <dt>Declared version</dt>
          <dd>{item.version_no}</dd>
          <dt>Locale</dt>
          <dd>{item.locale ?? "—"}</dd>
          <dt>Slot tool names on this site</dt>
          <dd className="mono">{item.slot_tool_names.length ? item.slot_tool_names.join(", ") : "None yet"}</dd>
          <dt>Declared tools</dt>
          <dd>
            <ul className="stack-sm">
              {item.tools.map((t) => (
                <li key={t.name}>
                  <code>{t.name}</code>
                  {t.category ? <span className="small muted"> · {t.category}</span> : null} — <span className="staff-inert">{t.description}</span>
                </li>
              ))}
            </ul>
          </dd>
          <dt>Waiting since</dt>
          <dd>{when(item.created_at)}</dd>
        </dl>
      </Card>
      <SiteRateLimitControl siteId={item.site_id} />
      {res?.applied ? (
        <Banner tone="success" live="polite" title={res.result === "approved" ? "Approved" : "Rejected"}>
          <Link to="/staff/review">Back to the queue</Link>
        </Banner>
      ) : (
        <Card title="Decision">
          <form className="stack" onSubmit={submit} noValidate>
            <RadioGroup
              legend="Decision"
              name="site-tools-decision"
              value={decision}
              onChange={setDecision}
              options={[
                { value: "approve", label: "Approve" },
                { value: "reject", label: "Reject" },
              ]}
            />
            {decision === "approve" ? (
              <fieldset className="stack-sm">
                <legend className="field-label">Classification — must name exactly the declared tools</legend>
                {classification.map((c, i) => (
                  <div key={c.name} className="row" style={{ gap: "var(--space-2)", alignItems: "flex-end" }}>
                    <Input
                      label={c.name}
                      mono
                      optional
                      value={c.category}
                      onChange={(e) => setClassification((xs) => xs.map((x, j) => (j === i ? { ...x, category: e.target.value } : x)))}
                      hint="A GET /v1/targeting/categories key, or leave empty for general-purpose."
                    />
                  </div>
                ))}
              </fieldset>
            ) : null}
            <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
            <StepUpNote required={false} />
            {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
            {res && !res.applied ? <NotAppliedBanner reason={res.result} result={res.result} /> : null}
            {cmd.error ? <StaffRejection error={cmd.error} /> : null}
            <div>
              <Button type="submit" loading={cmd.isPending}>
                Record decision
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}

/** G-34 console: approve or reject a slot's pricing-model change, migration 0066's review_slot_version. */
function SlotVersionReview({ item }: { item: ReviewQueueSlotVersionItem }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [decision, setDecision] = useState<"approve" | "reject" | "">("");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand(
    (req: { decision: "approve" | "reject"; reason: string }, opts) => ep.staff.review.slotVersion(item.slot_version_id, req, opts),
    (res: ReviewSlotVersionResponse) => {
      if (res.applied) void qc.invalidateQueries({ queryKey: staffKeys.reviewQueue });
    },
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!decision) return setFormError("Choose approve or reject.");
    const re = reasonError(reason);
    if (re) return setFormError(re);
    setFormError(null);
    cmd.run({ decision, reason: reason.trim() });
  }

  const res = cmd.data;
  return (
    <div className="stack">
      <Card title="Slot pricing-model change">
        <dl className="definition-list">
          <dt>Slot</dt>
          <dd>
            <code>{item.tool_name}</code> on {item.domain} · publisher <span className="mono">{shortId(item.publisher_id)}</span>
          </dd>
          <dt>Requested version</dt>
          <dd>
            {item.version_no} · {item.pricing_model.toUpperCase()}
          </dd>
          <dt>Live version</dt>
          <dd>{item.live_version ? `${item.live_version.pricing_model.toUpperCase()} (${item.live_version.tool_name})` : "None yet"}</dd>
          <dt>Slot status</dt>
          <dd>{item.slot_status.replace(/_/g, " ")}</dd>
          <dt>Waiting since</dt>
          <dd>{when(item.created_at)}</dd>
        </dl>
      </Card>
      {res?.applied ? (
        <Banner tone="success" live="polite" title={res.result === "approved" ? "Approved" : "Rejected"}>
          <Link to="/staff/review">Back to the queue</Link>
        </Banner>
      ) : (
        <Card title="Decision">
          <form className="stack" onSubmit={submit} noValidate>
            <RadioGroup
              legend="Decision"
              name="slot-version-decision"
              value={decision}
              onChange={setDecision}
              options={[
                { value: "approve", label: "Approve — this version goes live" },
                { value: "reject", label: "Reject" },
              ]}
            />
            <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
            <StepUpNote required={false} />
            {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
            {res && !res.applied ? <NotAppliedBanner reason={res.result} result={res.result} /> : null}
            {cmd.error ? <StaffRejection error={cmd.error} /> : null}
            <div>
              <Button type="submit" loading={cmd.isPending}>
                Record decision
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}

function VersionDecision({ kind, id, regulated }: { kind: "config_version" | "creative_version"; id: string; regulated: string[] }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const [decision, setDecision] = useState<"approve" | "reject" | "">("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand(
    (req: { decision: "approve" | "reject"; reason: string; regulated_categories_confirmed?: string[] }, opts) =>
      kind === "config_version" ? ep.staff.review.config(id, req, opts) : ep.staff.review.creative(id, req, opts),
    (res: ReviewVersionResponse) => {
      if (res.applied) void qc.invalidateQueries({ queryKey: staffKeys.reviewQueue });
    },
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!decision) return setFormError("Choose approve or reject.");
    const re = reasonError(reason);
    if (re) return setFormError(re);
    if (decision === "approve" && regulated.some((c) => !confirmed.includes(c))) {
      return setFormError("Confirm every regulated category to approve.");
    }
    setFormError(null);
    cmd.run({
      decision,
      reason: reason.trim(),
      ...(kind === "config_version" && decision === "approve" ? { regulated_categories_confirmed: confirmed } : {}),
    });
  }

  const res = cmd.data;
  if (res?.applied) {
    return (
      <Banner tone="success" live="polite" title={res.result === "approved" ? "Approved" : res.result === "rejected" ? "Rejected" : `Done: ${res.result}`}>
        {res.regulated_categories_confirmed?.length ? `Regulated categories confirmed: ${res.regulated_categories_confirmed.join(", ")}. ` : null}
        <Link to="/staff/review">Back to the queue</Link>
      </Banner>
    );
  }

  return (
    <Card title="Decision">
      <form className="stack" onSubmit={submit} noValidate>
        <RadioGroup
          legend="Decision"
          name="decision"
          value={decision}
          onChange={setDecision}
          options={[
            { value: "approve", label: "Approve" },
            { value: "reject", label: "Reject" },
          ]}
        />
        {regulated.length ? (
          <fieldset className="stack-sm">
            <legend className="field-label">Regulated categories: confirm each one to approve</legend>
            {regulated.map((c) => (
              <Checkbox
                key={c}
                label={`I've reviewed this version for ${c}`}
                checked={confirmed.includes(c)}
                onChange={(e) => setConfirmed((xs) => (e.target.checked ? [...xs, c] : xs.filter((x) => x !== c)))}
              />
            ))}
          </fieldset>
        ) : null}
        <Textarea label="Reason" hint="Recorded with the review; a rejection's reason is what the advertiser sees." value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        <StepUpNote required={false} />
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {res && !res.applied ? (
          <NotAppliedBanner reason={res.reason} result={res.result}>
            {res.regulated_categories?.length ? <span>Confirm: {res.regulated_categories.join(", ")}.</span> : null}
            {res.categories?.length ? <span>Not cleared: {res.categories.join(", ")}.</span> : null}
            {res.status ? <span>The version is {res.status.replace("_", " ")}.</span> : null}
          </NotAppliedBanner>
        ) : null}
        {cmd.error ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" loading={cmd.isPending}>
            Record decision
          </Button>
        </div>
      </form>
    </Card>
  );
}

function AppealDecision({ item }: { item: ReviewQueueAppealItem }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const { me } = useStaffSession();
  const [decision, setDecision] = useState<"grant" | "deny" | "">("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const cmd = useStaffCommand(
    (req: { decision: "grant" | "deny"; reason: string; regulated_categories_confirmed?: string[] }, opts) => ep.staff.review.decideAppeal(item.appeal_id, req, opts),
    () => void qc.invalidateQueries({ queryKey: staffKeys.reviewQueue }),
  );
  const ownRejection = !!item.rejection && item.rejection.reviewer_user_id === me.user_id;
  // A grant of a config version re-runs its approval: a 422
  // regulated_review_required names the categories to confirm (0061:928-931, 691-692).
  const toConfirm =
    cmd.error instanceof ApiError && cmd.error.code === "regulated_review_required" && Array.isArray(cmd.error.detail?.regulated_categories)
      ? (cmd.error.detail.regulated_categories as unknown[]).filter((c): c is string => typeof c === "string")
      : [];

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!decision) return setFormError("Choose grant or deny.");
    const re = reasonError(reason);
    if (re) return setFormError(re);
    setFormError(null);
    cmd.run({ decision, reason: reason.trim(), ...(decision === "grant" && confirmed.length ? { regulated_categories_confirmed: confirmed } : {}) });
  }

  return (
    <div className="stack">
      <Card title="Appeal">
        <dl className="definition-list">
          <dt>Contests</dt>
          <dd>
            {item.version_kind.replace("_", " ")} <span className="mono">{item.version_id}</span>
          </dd>
          <dt>Campaign / advertiser</dt>
          <dd className="mono">
            {item.campaign_id} / {item.advertiser_id}
          </dd>
          <dt>Basis</dt>
          <dd>
            <pre className="staff-inert">{item.basis}</pre>
          </dd>
          <dt>Filed</dt>
          <dd>{when(item.created_at)}</dd>
          <dt>Rejection</dt>
          <dd>
            {item.rejection ? (
              <>
                “{item.rejection.reason ?? "no reason recorded"}” by{" "}
                <span className="mono">{ownRejection ? "you" : shortId(item.rejection.reviewer_user_id)}</span>, {when(item.rejection.rejected_at)}
              </>
            ) : (
              "No linked rejection event"
            )}
          </dd>
        </dl>
      </Card>
      {cmd.data ? (
        <Banner tone="success" live="polite" title={cmd.data.result === "granted" ? "Appeal granted" : "Appeal denied"}>
          The version is now {cmd.data.version_status.replace("_", " ")}. <Link to="/staff/review">Back to the queue</Link>
        </Banner>
      ) : (
        <Card title="Decision">
          {ownRejection ? (
            <Banner tone="warning" title="A different reviewer has to decide this">
              You made the rejection this appeal contests. The server refuses your decision (appeal_same_reviewer).
            </Banner>
          ) : null}
          <form className="stack" onSubmit={submit} noValidate>
            <RadioGroup
              legend="Decision"
              name="appeal-decision"
              value={decision}
              onChange={setDecision}
              options={[
                { value: "grant", label: "Grant", hint: "Re-runs the version's approval with every gate; a gate's refusal refuses the grant." },
                { value: "deny", label: "Deny", hint: "The rejection stands." },
              ]}
            />
            {toConfirm.length ? (
              <fieldset className="stack-sm">
                <legend className="field-label">Regulated categories: confirm each one to grant</legend>
                {toConfirm.map((c) => (
                  <Checkbox
                    key={c}
                    label={`I've reviewed this version for ${c}`}
                    checked={confirmed.includes(c)}
                    onChange={(e) => setConfirmed((xs) => (e.target.checked ? [...xs, c] : xs.filter((x) => x !== c)))}
                  />
                ))}
              </fieldset>
            ) : null}
            <Textarea label="Reason" hint="1–2,000 characters; the advertiser sees it." value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
            <StepUpNote required />
            {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
            {cmd.error ? <StaffRejection error={cmd.error} /> : null}
            <div>
              <Button type="submit" loading={cmd.isPending} disabled={ownRejection}>
                Record decision
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
